
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function AboutCTA() {
  const [formData, setFormData] = useState({ nombre: '', email: '', mensaje: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('nombre', formData.nombre);
      body.append('email', formData.email);
      body.append('mensaje', formData.mensaje);
      await fetch('https://readdy.ai/api/form/d6acg0fmvg9ih2c7agq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection animation="slide-left">
            <div className="space-y-6">
              <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest">Contáctanos</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                ¿Quieres saber más sobre <span className="font-display italic text-emerald-600">nosotros</span>?
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Estamos siempre abiertos a conversar. Ya sea que quieras unirte a nuestro equipo, ser partner o simplemente conocernos mejor.
              </p>
              <div className="space-y-4 pt-4">
                {[
                  { icon: 'ri-mail-line', text: 'info@lamillefera.tech' },
                  { icon: 'ri-map-pin-line', text: 'Miami, Florida, Estados Unidos' },
                  { icon: 'ri-global-line', text: 'Operaciones en 12 países' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-emerald-600 text-base`}></i>
                    </div>
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <Link to="/precios" className="inline-flex items-center text-emerald-600 font-semibold text-sm hover:text-emerald-700 transition-colors cursor-pointer">
                  <span className="whitespace-nowrap">Ver planes y precios</span>
                  <i className="ri-arrow-right-line ml-2 text-base"></i>
                </Link>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="slide-right">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              {submitted ? (
                <div className="text-center py-12 space-y-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-check-line text-emerald-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">¡Mensaje enviado!</h3>
                  <p className="text-sm text-gray-500">Te responderemos en las próximas 24 horas.</p>
                </div>
              ) : (
                <form id="about-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Envíanos un mensaje</h3>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mensaje</label>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      placeholder="¿En qué podemos ayudarte?"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    ></textarea>
                    <p className="text-[10px] text-gray-400 mt-1 text-right">{formData.mensaje.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? 'Enviando...' : 'Enviar Mensaje'}
                  </button>
                </form>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
